import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getDevicesApi,
  getDeviceApi,
  updateDeviceApi,
  activateDeviceApi,
  deactivateDeviceApi,
  classifyDeviceApi,
} from "../api/devicesApi";
import type {
  RestClassifyDevice,
  RestUpdateDevice,
} from "@KassaroAB/shared/types/device";
import type { StoreId, DeviceId } from "@KassaroAB/shared/types/ids";

export const useGetDevicesQuery = (storeId: StoreId | null) => {
  return useQuery({
    queryKey: ["devices", storeId],
    queryFn: () => getDevicesApi(storeId!),
    enabled: !!storeId,
  });
};

export const useGetDeviceQuery = (
  storeId: StoreId | null,
  deviceId: DeviceId | null,
) => {
  return useQuery({
    queryKey: ["device", storeId, deviceId],
    queryFn: () => getDeviceApi(storeId!, deviceId!),
    enabled: !!storeId && !!deviceId,
  });
};

export const useUpdateDeviceMutation = (storeId: StoreId | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      deviceId,
      device,
    }: {
      deviceId: DeviceId;
      device: RestUpdateDevice;
    }) => updateDeviceApi(storeId!, deviceId, device),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["devices", storeId] });
      queryClient.invalidateQueries({ queryKey: ["device", storeId] });
    },
  });
};

const useDeviceMutation = <TVariables extends { deviceId: DeviceId }>(
  storeId: StoreId | null,
  mutationFn: (storeId: StoreId, variables: TVariables) => Promise<unknown>,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: TVariables) => mutationFn(storeId!, variables),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["devices", storeId] });
      queryClient.invalidateQueries({ queryKey: ["device", storeId] });
      queryClient.invalidateQueries({
        queryKey: ["stores", storeId, "billingItems"],
      });
      queryClient.invalidateQueries({
        queryKey: ["stores", storeId, "entitlements"],
      });
    },
  });
};

export const useActivateDeviceMutation = (storeId: StoreId | null) =>
  useDeviceMutation(storeId, (currentStoreId, { deviceId }) =>
    activateDeviceApi(currentStoreId, deviceId),
  );

export const useDeactivateDeviceMutation = (storeId: StoreId | null) =>
  useDeviceMutation(storeId, (currentStoreId, { deviceId }) =>
    deactivateDeviceApi(currentStoreId, deviceId),
  );

export const useClassifyDeviceMutation = (storeId: StoreId | null) =>
  useDeviceMutation<{
    deviceId: DeviceId;
    classification: RestClassifyDevice;
  }>(storeId, (currentStoreId, { deviceId, classification }) =>
    classifyDeviceApi(currentStoreId, deviceId, classification),
  );

